import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import api from "@/lib/api";

/**
 * Parent Project Picker — 149-aj (feature: user request "WO yang sudah diinput bisa
 * merubah parent project").
 *
 * 149-cw-r114 (2026-08-29): Panel ganti parent DITUTUP secara default. User klik
 * tombol "Ganti Parent" untuk expand → search + list muncul. Alasan: mencegah
 * daftar parent muncul otomatis saat buka edit dialog (mengganggu fokus edit).
 *
 * Kegunaan:
 *  - Load daftar semua project bertipe `new_project` yang aktif (lazy load saat panel dibuka)
 *  - Tampilkan searchable list untuk pindah parent WO/VO
 *  - Highlight parent saat ini + peringatan visual bila parent berubah (client / lokasi)
 */
export default function ParentProjectPicker({ project, value, onChange, disabled }) {
  const [parents, setParents] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  // Lazy load parents saat panel dibuka pertama kali.
  useEffect(() => {
    if (!panelOpen || loadedOnce) return;
    setLoading(true);
    api.get("/active-projects")
      .then(({ data }) => {
        const eligible = (data || []).filter(
          (p) => p.project_type === "new_project" && p.status !== "cancelled"
        );
        setParents(eligible);
        setLoadedOnce(true);
      })
      .catch((e) => console.warn("Failed to load parent candidates", e))
      .finally(() => setLoading(false));
  }, [panelOpen, loadedOnce]);

  const currentParent = useMemo(
    () => parents.find((p) => p.id === value) || null,
    [parents, value]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return parents;
    return parents.filter((p) => {
      const hay = [p.project_name, p.client, p.city, p.province]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [q, parents]);

  const originalParentId = project?.parent_project_id;
  const changed = value && originalParentId && value !== originalParentId;

  return (
    <div className="space-y-1.5" data-testid="parent-picker-wrap">
      <Label className="overline text-slate-600 flex items-center justify-between">
        <span>Parent Project {disabled && <span className="normal-case text-slate-400 italic text-[10px]">(read-only)</span>}</span>
        {changed && (
          <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5" data-testid="parent-changed-badge">
            PARENT BERUBAH
          </span>
        )}
      </Label>

      {/* Current parent chip + toggle button */}
      <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs flex items-start justify-between gap-3" data-testid="parent-picker-current">
        <div className="min-w-0 flex-1">
          {currentParent || (originalParentId && !loadedOnce) ? (
            <>
              <div className="font-semibold text-[#0B1D3A] truncate">
                {currentParent?.project_name || project?.parent_project_name || "Parent tersimpan"}
              </div>
              <div className="text-slate-500 text-[10px] truncate">
                {currentParent?.client || project?.client || "-"}
                {(currentParent?.city || currentParent?.province) && ` · ${[currentParent.city, currentParent.province].filter(Boolean).join(", ")}`}
              </div>
            </>
          ) : (
            <div className="text-slate-400 italic">Parent belum dipilih</div>
          )}
        </div>
        {!disabled && (
          <button
            type="button"
            data-testid="parent-picker-toggle"
            onClick={() => setPanelOpen((v) => !v)}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-[#0B1D3A] hover:bg-slate-100 focus:outline-none focus:border-[#FF5A1F]"
            title={panelOpen ? "Tutup panel ganti parent" : "Buka panel ganti parent"}
          >
            {panelOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {panelOpen ? "Tutup" : "Ganti Parent"}
          </button>
        )}
      </div>

      {!disabled && panelOpen && (
        <div className="border border-slate-200 bg-white p-2 space-y-1.5" data-testid="parent-picker-panel">
          <div className="flex items-center gap-2">
            <input
              type="text"
              data-testid="parent-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari parent lain (nama / client / kota)…"
              className="flex-1 text-xs px-2 py-1.5 border border-slate-300 focus:outline-none focus:border-[#FF5A1F]"
              autoFocus
            />
            {changed && (
              <button
                type="button"
                data-testid="parent-picker-reset"
                onClick={() => onChange(originalParentId)}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#FF5A1F] border border-slate-300 bg-white"
                title="Kembalikan ke parent semula"
              >
                <RotateCcw size={10} /> Reset
              </button>
            )}
          </div>
          <div className="border border-slate-200 max-h-40 overflow-y-auto" data-testid="parent-picker-list">
            {loading && (
              <div className="text-[10px] text-slate-400 italic p-2">Memuat daftar parent…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-[10px] text-slate-400 italic p-2">Tidak ada parent yang cocok.</div>
            )}
            {!loading && filtered.map((p) => {
              const active = p.id === value;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`parent-picker-option-${p.id}`}
                  onClick={() => onChange(p.id)}
                  className={`w-full text-left px-2 py-1.5 text-[11px] border-b border-slate-100 hover:bg-amber-50 transition-colors ${active ? "bg-[#0B1D3A] text-white hover:bg-[#0B1D3A]" : "bg-white text-slate-700"}`}
                >
                  <div className="font-semibold flex items-center gap-1.5">
                    {active && <span className="text-[9px]">✓</span>}
                    {p.project_name}
                    {p.status === "completed" && (
                      <span className={`text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500 border border-slate-300"}`}>
                        Completed
                      </span>
                    )}
                  </div>
                  <div className={`text-[10px] ${active ? "text-white/80" : "text-slate-500"}`}>
                    {p.client || "-"}
                    {(p.city || p.province) && ` · ${[p.city, p.province].filter(Boolean).join(", ")}`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!disabled && changed && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 p-2 leading-relaxed" data-testid="parent-change-warning">
          ⚠ <b>Parent akan pindah</b> saat Save. Sistem akan otomatis sync <span className="font-mono">client</span>, <span className="font-mono">lokasi</span>, dan <span className="font-mono">nama parent</span> ke parent baru. History perubahan direkam di <i>edit history</i>.
        </p>
      )}
    </div>
  );
}
